import React, { useRef } from "react";

import { WorkEnvironment } from "~/components/WorkEnvironment";
import {
  useAppContextMethods,
  useGlobalUserMethods,
  useGlobalUserState,
} from "~/state";
import { upsertHealthyWorkplace } from "~/util";

import writingImg from "../../assets/gfx/03_writing.jpg";

import {
  ModuleHeader,
  SettingFoundation,
  EducationActivities,
  BestPractice,
  ModuleQuizForm,
  useModuleState,
  ModuleSection,
  ModuleProvider,
  ModuleFooter,
} from "../../components";

interface ModuleProps {}

export const Module3: React.FC<ModuleProps> = ({}) => {
  const { nextModule } = useGlobalUserMethods();
  const user = useGlobalUserState();
  const [state, setState, { handleSectionChange }] = useModuleState();

  const { scrollToTop } = useAppContextMethods();

  const headerRef = useRef<HTMLElement>(null);
  const section1Ref = useRef<HTMLElement>(null);
  const section2Ref = useRef<HTMLElement>(null);
  const section3Ref = useRef<HTMLElement>(null);
  const section4Ref = useRef<HTMLElement>(null);
  const section5Ref = useRef<HTMLElement>(null);
  const section6Ref = useRef<HTMLElement>(null);
  const section7Ref = useRef<HTMLElement>(null);
  const section8Ref = useRef<HTMLElement>(null);

  const QUIZ_3 = [
    {
      question:
        "What elements produce a successful health and wellbeing program? You can select more than one answer!",
      options: ["Policy", "Environment", "Education & activities"],
      answer: [0, 1, 2],
      related: section2Ref.current,
    },
    {
      question: "A health policy is:",
      options: [
        "A workplace health and wellbeing program",
        "A written document formalising an organisation’s commitment to workplace health and wellbeing",
        "The same as an occupational health and safety policy",
      ],
      answer: [1],
      related: section4Ref.current,
    },
    {
      question: "Supportive workplace environments:",
      options: [
        "Include the external surrounding parks, reserves, cafes and shopping centres",
        "Include the internal infrastructure and facilities provided to staff and encourages healthy behaviours by providing the opportunity to make healthy choices",
      ],
      answer: [1],
      related: section5Ref.current,
    },
    {
      question:
        "Health and wellbeing education and activities (you can select more than one answer!):",
      options: [
        "Provides employees with health knowledge and opportunities to participate in healthy behaviours",
        "Are sufficient for a comprehensive health and wellbeing policy",
        "Should be considered once health policy and supportive environments are in place, to help build a healthy culture",
      ],
      answer: [0, 2],
      related: section6Ref.current,
    },
    {
      question:
        "If you were targeting unhealthy eating and drinking in the workplace, which statement would best reflect a combination of strategies addressing policy, environment and education and activities?",
      options: [
        "Providing healthy eating education sessions and coordinating a recipe club",
        "Implementing a healthy eating policy and providing healthy morning teas",
        "Ensuring kitchen and lunch room facilities are well equipped and implementing a healthy catering policy",
        "Ensuring kitchen and lunch room facilities are well equipped, implementing a healthy catering policy, providing healthy eating educational sessions to staff and coordinating a recipe club",
      ],
      answer: [3],
      related: section2Ref.current,
    },
  ];

  return (
    <ModuleProvider onSectionChange={handleSectionChange}>
      <ModuleHeader currentPage={3} totalPages={4} ref={headerRef} />

      <ModuleSection
        id="introduction"
        sectionNumber={1}
        sectionTitle="Introduction"
        className="mb-4"
        ref={section1Ref}
      >
        <div className="page-header block-blueberry">
          <h1>Best practice workplace health promotion</h1>
        </div>
      </ModuleSection>

      <ModuleSection
        id="keys-to-success"
        sectionNumber={2}
        sectionTitle="What are the keys to success"
        className="mb-4"
        ref={section2Ref}
      >
        <div className="row">
          <div className="col-md-10 mx-auto">
            <h2 className="h1 text-uppercase text-center text-warning mb-4">
              What are the keys to success?
            </h2>
            <div className="d-flex flex-column align-items-center">
              <p>
                When planning your workplace health and wellbeing program, we
                recommend including strategies from across each of these three
                areas:
              </p>
              <p className="lead text-center text-uppercase mb-3">
                Health policies <span className="text-gold">—</span> Workplace
                environment <span className="text-gold">—</span> Health
                education and activities
              </p>
              <p className="mb-3">
                Let’s take unhealthy eating as an example. Providing healthy
                eating education and information to staff may be ineffective if
                the workplace provides unhealthy catering at meetings and
                events. Introducing a healthy catering policy will ensure the
                healthy eating information and messages are consistent. The
                triangle below depicts the impact each of the elements has on
                changing behaviour, with policy having the largest impact.
              </p>
            </div>
            <EducationActivities />
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="policy-environment"
        sectionNumber={3}
        sectionTitle="Policy, Environment and Culture"
        className="module3__bg3 section-arrows"
        ref={section4Ref}
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <h2 className="h1 text-uppercase text-center my-4">
            Policy, environment and culture
          </h2>
          <div className="row mb-4">
            <div className="col-md-10 col-lg-8 mx-auto">
              <div className="embed-responsive embed-responsive-16by9 mb-4">
                <iframe
                  className="embed-responsive-item"
                  src="https://www.youtube-nocookie.com/embed/zgyZT4ENYvU"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="video-caption lead text-center">
                Professor Fiona Bull explains how policy, environment and
                culture can be used in a workplace health program to support
                behaviour change.
              </p>
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="setting-foundation"
        sectionNumber={4}
        sectionTitle="Setting the foundation"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-creating-a-workplace-health-policy.docx",
              title: "Creating a health and wellbeing policy template",
              type: "document",
            },
          ];
        }, [])}
        className="mb-4"
        ref={section5Ref}
      >
        <div className="row">
          <div className="col-md-8 col-lg-6 mx-auto">
            <h2 className="h1 text-warning text-uppercase text-center my-4">
              Setting the foundation
            </h2>
            <img
              className="img-fluid mb-4"
              src={writingImg}
              alt="Image of people writing"
            />
          </div>
          <div className="col-md-10 mx-auto">
            <SettingFoundation />
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="healthy-workplace-look-like"
        sectionNumber={5}
        sectionTitle="What does a healthy workplace look like?"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="module3__bg5 section-arrows mb-4"
        ref={section6Ref}
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <WorkEnvironment />
        </div>
      </ModuleSection>

      <ModuleSection
        id="education-activities"
        sectionNumber={6}
        sectionTitle="Education &amp; Activities"
        className="mb-4"
        ref={section3Ref}
      >
        <div className="row">
          <div className="col-md-10 mx-auto">
            <h2 className="h1 text-uppercase text-center text-warning mb-4">
              Education &amp; Activities
            </h2>
            <div
              className="mb-4"
              css={`
                column-count: 2;
                column-gap: 3rem;
              `}
            >
              <p>
                Once your policy is in place and the workplace environment is
                supportive of healthy behaviours, education and activities will
                help to build a healthy workplace culture by engaging staff
                directly in the program.
              </p>
              <p>
                Education provides employees with health knowledge, allowing
                them the opportunity to identify where they could make
                improvements in order to lead a healthier lifestyle and reduce
                their risk of chronic disease.
              </p>
              <p>
                Activities allow employees to actually participate in healthy
                behaviours. This provides them with an opportunity to learn new
                healthy living skills and can provide a kick start to forming a
                new healthy habit.
              </p>
              <p>
                For example, a cooking class could teach employees about healthy
                cooking as well as practice in preparing healthy meals. Also, a
                workplace exercise program could encourage employees to continue
                regular exercise, once the program has completed.
              </p>
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="best-practice"
        sectionNumber={7}
        sectionTitle="Best Practice"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-best-practice-guide.pdf",
              title:
                "Best practice guide: workplace health and wellbeing strategies",
              type: "document",
            },
          ];
        }, [])}
        className="mb-4"
        ref={section7Ref}
      >
        <h2 className="h1 text-warning text-uppercase text-center my-4">
          Best practice
        </h2>
        <BestPractice />
      </ModuleSection>

      <ModuleSection
        id="quiz"
        sectionNumber={8}
        sectionTitle="Quiz"
        className="px-3"
        ref={section8Ref}
      >
        <ModuleQuizForm
          quizHeading={`Answer the following ${
            QUIZ_3.length
          } questions to progress to module ${user.currentModule + 1}`}
          quizKey={3}
          data={QUIZ_3}
          onSuccess={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              completedModules: Array.from(
                { length: user.currentModule },
                (v, i) => i + 1,
              ).join(","),
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
          onNextClick={(e) => {
            e.preventDefault();
            scrollToTop();
            nextModule();
          }}
          onFail={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
        />
      </ModuleSection>
      <ModuleFooter
        moduleNumber={3}
        totalSections={8}
        currentSection={state.currentSection}
        sectionTitle={state.currentSectionTitle}
        sectionData={state.currentSectionData || []}
      />
    </ModuleProvider>
  );
};
